// src/components/MessageDetail.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import DOMPurify from 'dompurify';
import { Trash2, Mail } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/spinner';


export type UserMessage = {
  id: string;
  senderString?: string | null;
  recipientString?: string | null;
  subject: string;
  body: string;
  createdAt: string;
  toJson?: string[];
  ccJson?: string[];
  replyJson?: string[];
  attachments?: { filename: string; data: string; mimeType?: string }[];
  userWhitelist?: string[];
};

// helper to extract the single-element type from the optional attachments array
type Attachment = NonNullable<UserMessage['attachments']>[0];

// Reusable attachment preview + download
export const AttachmentView = ({ attachment }: { attachment: { filename: string; data: string; mimeType?: string } }) => {
  const { filename, data, mimeType } = attachment;
  const ext = filename.split('.').pop()?.toLowerCase();

  if (ext === 'pdf') {
    return (
      <div className="mb-6 h-full">
        <div className="text-sm font-medium text-gray-700 mb-2">{filename}</div>
        <object
          data={`data:${mimeType};base64,${data}`}
          type="application/pdf"
          width="100%"
          height="100%"
          className="border border-gray-200 rounded-lg h-full"
        >
          <p className="text-sm text-gray-500">
            PDF preview not supported.{' '}
            <a href={`data:${mimeType};base64,${data}`} download className="underline text-blue-600">
              Download
            </a>
          </p>
        </object>
      </div>
    );
  }

  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext || '')) {
    return (
      <div className="mb-6">
        <div className="text-sm font-medium text-gray-700 mb-2">{filename}</div>
        <img
          src={`data:${mimeType};base64,${data}`}
          alt={filename}
          className="w-full rounded-lg border border-gray-200"
        />
      </div>
    );
  }

  // Fallback: download link
  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-gray-700 mb-1">{filename}</div>
      <a href={`data:${mimeType};base64,${data}`} download className="text-sm underline text-blue-600">
        Download {filename}
      </a>
    </div>
  );
};

// Header for thread metadata
const TheadHeader = ({ thread }: any) => (
  <div className="mb-4">
    <div className="text-lg font-semibold truncate">{thread.subject}</div>
    <div className="text-sm text-gray-600 mt-1">
      {thread.senderString} | {new Date(thread.createdAt).toLocaleString()}
    </div>
  </div>
);

// Single message rendering: HTML body + attachments popup
const ThreadMessage = ({ message }: { message: UserMessage }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  // Initialize as null, with correct element type
  const [currentAttachment, setCurrentAttachment] = useState<Attachment | null>(null);

  const openPreview = (att: Attachment) => {
    setCurrentAttachment(att);
    setPreviewOpen(true);
  };

  return (
    <div className="space-y-4">
      <div
        className="prose max-w-none"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.body) }}
      />

      {message.attachments?.length ? (
        <div className="inline-flex flex-wrap space-x-3 mb-4">
          {message.attachments.map((att, idx) => (
            <button
              key={idx}
              className="attachment-item"
              onClick={() => openPreview(att)}
            >
              {att.filename}
            </button>
          ))}
        </div>
      ) : null}

      {currentAttachment && (
        <div className="preview-content mb-4">
          {currentAttachment.mimeType?.startsWith('image/') ? (
            <img
              src={`data:${currentAttachment.mimeType};base64,${currentAttachment.data}`}
              alt={currentAttachment.filename}
              className="w-full rounded-lg border border-gray-200"
            />
          ) : (
            <a
              href={`data:${currentAttachment.mimeType};base64,${currentAttachment.data}`}
              download={currentAttachment.filename}
              className="underline text-blue-600"
            >
              Download {currentAttachment.filename}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// Main thread detail with messages list
export const ThreadDetail = ({ getThread, onDelete, onReply, thread }: any) => {
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const threadId = thread.id;
  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState<Attachment | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [threadDetails, setThreadDetails] = useState<any>(thread);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log("threadId", threadId);
    setLoading(true);
    setMessages([]); // Clear previous messages immediately
    
    getThread(threadId).then(({ data, error }: any) => {
      console.log("getThread response - data:", data);
      console.log("getThread response - error:", error);
      
      if (error) {
        console.error("GraphQL error:", error);
        setLoading(false);
        return;
      }
      
      if (!data) {
        console.error("No data returned from getThread");
        setLoading(false);
        return;
      }
      
      const messageData = data.getUserMessage || data.getUserMessageByToken;
      console.log("messageData:", messageData);
      
      if (!messageData) {
        console.error("No getUserMessage or getUserMessageByToken in response");
        setLoading(false);
        return;
      }
      
      setMessages(messageData.messages || []);
      setThreadDetails(messageData);
      setLoading(false);
    }).catch((err: any) => {
      console.error("Error in getThread:", err);
      setLoading(false);
    });
  }, [threadId]);

  const allAttachments = useMemo(() =>
    messages.flatMap(msg => msg.attachments || []).map(att => ({
      filename: att.filename,
      data: att.data,
      mimeType: att.mimeType
    })),
  [messages]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full bg-white rounded-lg p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header + actions - Mobile friendly */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <TheadHeader thread={thread} />
        </div>
        <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 sm:space-x-2">
          <Button
            variant="link"
            className="text-blue-600 inline-flex items-center justify-center space-x-1 p-2 sm:p-1"
            onClick={() => onReply(threadDetails)}
          >
            <Mail size={16} /> <span>Reply</span>
          </Button>
          <Button
            variant="link"
            className="text-red-600 inline-flex items-center justify-center space-x-1 p-2 sm:p-1"
            onClick={() => setConfirmOpen(true)}
          >
            <Trash2 size={16} /> <span>Delete</span>
          </Button>
        </div>
      </div>

      {/* Thread meta */}
     

      {/* Inline list of attachments, each opens preview modal */}
      <h3 className="text-lg font-semibold text-gray-900">Attachments</h3>
      {allAttachments.length > 0 && (
        <div className="inline-flex flex-wrap space-x-4 mb-4">
          {allAttachments.map((att, idx) => (
            <Button
              key={idx}
              variant="link"
              className="text-blue-600"
              onClick={() => {
                setCurrentAttachment(att);
                setPreviewOpen(true);
              }}
            >
              {att.filename}
            </Button>
          ))}
        </div>
      )}

      {/* Single-attachment preview modal */}
      {currentAttachment && (
        <Dialog
          open={previewOpen}
          onOpenChange={(open: boolean) => {
            if (!open) {
              setPreviewOpen(false);
            }
          }}
        >
          <DialogContent
            className="max-w-none"
            style={{ width: '80%', height: '90vh', margin: 'auto', padding: '1.5rem' }}
            onInteractOutside={(event) => event.preventDefault()}
          >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold truncate">
              {currentAttachment.filename}
            </h2>
            <div className="flex space-x-4 text-sm">
              <a
                href={`data:${currentAttachment.mimeType};base64,${currentAttachment.data}`}
                download={currentAttachment.filename}
                className="underline text-blue-600"
              >
                Download
              </a>
            </div>
          </div>
          {/* Use AttachmentView to render PDF or image correctly */}
          <AttachmentView
            attachment={{
              filename: currentAttachment.filename,
              data: currentAttachment.data,
              mimeType: currentAttachment.mimeType
            }}
          />
          </DialogContent>
        </Dialog>
      )}

      {/* Messages with inline attachments */}
      {messages.map((msg, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg bg-white shadow mb-6"
        >
          {/* Message header */}
          <div className="flex justify-between items-center px-4 pt-4 pb-2 border-b border-gray-200">
            <span className="font-medium text-gray-800 font-bold">
              From: {msg.senderString ?? 'Unknown Sender'}
             
            </span>
            <span className="font-medium text-gray-800">
              
              To: {msg.recipientString}
            </span>
            <span className="text-sm text-gray-600">
              {new Date(msg.createdAt).toLocaleString()}
            </span>
          </div>

          {/* Message body */}
          <div
            className="prose max-w-none px-4 py-4"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.body) }}
          />

          {/* Inline attachments */}
          {msg.attachments && msg.attachments.length > 0 && (
            <div className="inline-flex flex-wrap space-x-3 px-4 pb-4">
              {msg.attachments.map((att, i) => (
                <button
                  key={i}
                  className="underline text-blue-600"
                  onClick={() => {
                    setCurrentAttachment(att);
                    setPreviewOpen(true);
                  }}
                >
                  {att.filename}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Global attachments modal */}
      <Dialog
        open={modalOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setModalOpen(false);
          }
        }}
      >
        <DialogContent
          className="max-w-none"
          style={{ width: '80%', height: '90vh', margin: 'auto' }}
          onInteractOutside={(event) => event.preventDefault()}
        >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">All Attachments</h2>
        </div>
        <div className="space-y-6 overflow-auto">
          {allAttachments.map((att, idx) => (
            <AttachmentView key={idx} attachment={att} />
          ))}
        </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation modal */}
      <Dialog
        open={confirmOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setConfirmOpen(false);
          }
        }}
      >
        <DialogContent
          className="max-w-none"
          style={{ width: '30%', height: '30%', margin: 'auto', padding: '1.5rem' }}
          onInteractOutside={(event) => event.preventDefault()}
        >
        <div className="flex flex-col items-center justify-between h-full">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete?</h2>
          <div className="flex space-x-4">
            <Button
              variant="link"
              className="text-blue-600 inline-flex items-center space-x-1"
              onClick={() => { onDelete(thread.id); setConfirmOpen(false); }}
            >
              <Trash2 size={16} /> <span>Yes, Delete</span>
            </Button>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          </div>
        </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
